import React, { useEffect, useRef, useState } from "react";
import { Avatar, Select, Tag, message } from "antd";
import { getUserLabel } from "../../helper/userDisplay";

/**
 * Inline inspector assignment for a property. Renders the assigned inspectors as
 * closeable initial-chips inside a multi-select; an empty value shows an
 * "Assign…" affordance. Edits commit to the property's assignedInspectors when
 * the control loses focus (so toggling several at once is one write). The Cloud
 * Functions (ownerVisitSync) mirror the change onto open inspections + notify
 * anyone newly added.
 *
 * Props:
 *   value     array of inspector uids currently assigned
 *   options   assignable inspector user docs ({ _id, name, email })
 *   onSave    async (uids) => void  — persists the new set
 *   disabled  bool
 *   style     style overrides for the Select
 */
const COLORS = ["#7c6f64", "#a06a3f", "#4f6d7a", "#6a8d73", "#9a6a8d", "#8d6a6a"];
const colorFor = (key) => {
  let h = 0;
  for (let i = 0; i < String(key).length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return COLORS[Math.abs(h) % COLORS.length];
};
const initialsOf = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0] || "")
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

const sameSet = (a, b) =>
  [...a].sort().join(" ") === [...b].sort().join(" ");

const AssignInspectors = ({ value = [], options = [], onSave, disabled, style }) => {
  const [local, setLocal] = useState(value);
  const [saving, setSaving] = useState(false);
  const savedRef = useRef(value);

  // Re-sync when the persisted value changes from outside (realtime stream),
  // unless the user is mid-edit with unsaved changes.
  useEffect(() => {
    if (sameSet(local, savedRef.current)) setLocal(value);
    savedRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.join(" ")]);

  const commit = async () => {
    if (sameSet(local, savedRef.current)) return; // nothing changed
    const next = local;
    setSaving(true);
    try {
      await onSave(next);
      savedRef.current = next;
    } catch (e) {
      message.error("Could not update inspectors");
      setLocal(savedRef.current); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  const tagRender = ({ label, value: uid, closable, onClose }) => {
    // label comes from the option (getUserLabel: name -> email -> id); if the
    // assigned uid is no longer in options, fall back to the uid itself.
    const name = String(label || uid || "—");
    // Real names collapse to the first word for a compact chip; emails/ids
    // (no spaces) show whole.
    const first = name.split(/\s+/)[0] || name;
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, paddingInlineStart: 2, marginInlineEnd: 4 }}
      >
        <Avatar size={18} style={{ backgroundColor: colorFor(uid), fontSize: 9 }}>
          {initialsOf(name)}
        </Avatar>
        {first}
      </Tag>
    );
  };

  return (
    <Select
      mode="multiple"
      size="small"
      style={{ minWidth: 220, ...style }}
      placeholder="Assign…"
      value={local}
      disabled={disabled || saving}
      loading={saving}
      onChange={setLocal}
      onBlur={commit}
      onInputKeyDown={(e) => {
        if (e.key === "Escape") {
          setLocal(savedRef.current); // cancel edits
          e.currentTarget.blur();
        }
      }}
      optionFilterProp="children"
      showSearch
      maxTagCount="responsive"
      tagRender={tagRender}
    >
      {options.map((u) => (
        <Select.Option key={u._id} value={u._id} label={getUserLabel(u)}>
          {getUserLabel(u)}
        </Select.Option>
      ))}
    </Select>
  );
};

export default AssignInspectors;
